import { FormEvent, useCallback, useEffect, useRef, useState } from "react"
import { MunicipioIbgeSelect } from "../../components/app/MunicipioIbgeSelect"
import { Tabs } from "../../components/app/Tabs"
import { useEmpresa } from "../../app/context/EmpresaContext"
import { apiFetch } from "../../lib/api"

const UF_OPTIONS = [
  "AC", "AL", "AP", "AM", "BA", "CE", "DF", "ES", "GO", "MA", "MT", "MS", "MG", "PA", "PB", "PR", "PE", "PI", "RJ", "RN", "RS", "RO", "RR", "SC", "SP", "SE", "TO",
]

const onlyDigits = (value: string) => value.replace(/\D/g, "")

type EmpresaForm = {
  razao_social: string
  nome_fantasia: string
  cnpj: string
  inscricao_estadual: string
  uf: string
  codigo_municipio: string
  municipio: string
  logradouro: string
  numero: string
  complemento: string
  bairro: string
  cep: string
  telefone: string
  regime_tributario: string
  grupo_id: string
}

type CnpjPrefill = {
  razao_social: string
  nome_fantasia: string | null
  cnpj: string
  inscricao_estadual: string
  uf: string
  codigo_municipio: string
  municipio_nome: string | null
  logradouro: string
  numero: string
  complemento: string
  bairro: string
  cep: string
  telefone: string
  regime_tributario: number
  situacao: string | null
  avisos: string[]
}

const emptyEmpresaForm = (): EmpresaForm => ({
  razao_social: "",
  nome_fantasia: "",
  cnpj: "",
  inscricao_estadual: "",
  uf: "SP",
  codigo_municipio: "",
  municipio: "",
  logradouro: "",
  numero: "",
  complemento: "",
  bairro: "",
  cep: "",
  telefone: "",
  regime_tributario: "1",
  grupo_id: "",
})

const parseApiError = (err: unknown) => {
  if (!(err instanceof Error)) return "Erro ao salvar"
  try {
    const parsed = JSON.parse(err.message) as { detail?: string | Array<{ msg?: string }> }
    if (typeof parsed.detail === "string") return parsed.detail
    if (Array.isArray(parsed.detail)) return parsed.detail.map((d) => d.msg ?? "").filter(Boolean).join("; ") || err.message
  } catch {
    /* texto bruto */
  }
  return err.message || "Erro ao salvar"
}

type SettingsResponse = {
  settings: Record<string, Record<string, unknown>>
  env_fallback: { smtp_configured: boolean; mp_configured: boolean }
}

type OrgRow = { id: string; nome: string; ativo: boolean }
type GrupoRow = { id: string; nome: string; ativo: boolean }
type EmpresaRow = {
  id: string
  cnpj: string
  razao_social: string
  nome_fantasia: string | null
  inscricao_estadual: string
  uf: string
  codigo_municipio: string
  municipio: string | null
  logradouro: string | null
  numero: string | null
  complemento: string | null
  bairro: string | null
  cep: string | null
  telefone: string | null
  regime_tributario: number
  grupo_id: string | null
  ativo: boolean
}
type MembershipRow = { id: string; email: string; papel: string; ativo: boolean }

const SUPER_TABS = [
  { id: "plataforma", label: "Plataforma" },
  { id: "suporte", label: "Suporte" },
  { id: "email", label: "E-mail" },
  { id: "operacao", label: "Operação" },
  { id: "billing", label: "Billing" },
  { id: "tenancy", label: "Organizações" },
]

export const SuperAdminPage = () => {
  const { reload: reloadEmpresaContext } = useEmpresa()
  const [tab, setTab] = useState("plataforma")
  const [data, setData] = useState<SettingsResponse | null>(null)
  const [message, setMessage] = useState("")
  const [error, setError] = useState("")
  const [orgs, setOrgs] = useState<OrgRow[]>([])
  const [selectedOrg, setSelectedOrg] = useState("")
  const [orgNomeDraft, setOrgNomeDraft] = useState("")
  const [empresas, setEmpresas] = useState<EmpresaRow[]>([])
  const [grupos, setGrupos] = useState<GrupoRow[]>([])
  const [memberships, setMemberships] = useState<MembershipRow[]>([])
  const [editingEmpresaId, setEditingEmpresaId] = useState<string | null>(null)
  const [editingGrupoId, setEditingGrupoId] = useState<string | null>(null)
  const [grupoNomeDraft, setGrupoNomeDraft] = useState("")
  const empresaFormRef = useRef<HTMLFormElement>(null)
  const [health, setHealth] = useState<Record<string, unknown> | null>(null)
  const [testEmail, setTestEmail] = useState("")
  const [orgSaving, setOrgSaving] = useState(false)
  const [grupoSaving, setGrupoSaving] = useState(false)
  const [empresaSaving, setEmpresaSaving] = useState(false)
  const [empresaForm, setEmpresaForm] = useState<EmpresaForm>(emptyEmpresaForm)
  const [cnpjLoading, setCnpjLoading] = useState(false)
  const [municipioNome, setMunicipioNome] = useState("")
  const [cnpjAvisos, setCnpjAvisos] = useState<string[]>([])

  const reloadSettings = () => apiFetch<SettingsResponse>("/v1/admin/settings").then(setData)

  const reloadOrgs = useCallback(async () => {
    const list = await apiFetch<OrgRow[]>("/v1/admin/organizacoes")
    setOrgs(list)
    return list
  }, [])

  const reloadTenancy = useCallback(async (orgId: string) => {
    const [empList, membList, grupoList] = await Promise.all([
      apiFetch<EmpresaRow[]>(`/v1/admin/organizacoes/${orgId}/empresas`),
      apiFetch<MembershipRow[]>(`/v1/admin/organizacoes/${orgId}/memberships`),
      apiFetch<GrupoRow[]>(`/v1/admin/organizacoes/${orgId}/grupos`),
    ])
    setEmpresas(empList)
    setMemberships(membList)
    setGrupos(grupoList)
  }, [])

  useEffect(() => {
    reloadSettings()
    reloadOrgs().catch((e) => setError(parseApiError(e)))
  }, [reloadOrgs])

  useEffect(() => {
    if (orgs.length > 0 && !selectedOrg) {
      setSelectedOrg(orgs[0].id)
    }
  }, [orgs, selectedOrg])

  useEffect(() => {
    const row = orgs.find((org) => org.id === selectedOrg)
    setOrgNomeDraft(row?.nome ?? "")
  }, [selectedOrg, orgs])

  useEffect(() => {
    setEditingEmpresaId(null)
    setEditingGrupoId(null)
    setEmpresaForm(emptyEmpresaForm())
    setMunicipioNome("")
    setCnpjAvisos([])
  }, [selectedOrg])

  useEffect(() => {
    if (!selectedOrg) return
    reloadTenancy(selectedOrg).catch((e) => setError(parseApiError(e)))
  }, [selectedOrg, reloadTenancy])

  useEffect(() => {
    if (tab === "operacao") {
      apiFetch<Record<string, unknown>>("/v1/admin/ops/health").then(setHealth)
    }
  }, [tab])

  const saveKey = async (chave: string, valor: Record<string, unknown>) => {
    await apiFetch(`/v1/admin/settings/${chave}`, { method: "PUT", body: JSON.stringify({ valor }) })
    setMessage(`Salvo: ${chave}`)
    await reloadSettings()
  }

  const handlePlataforma = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const fd = new FormData(e.currentTarget)
    await saveKey("branding", {
      app_name: String(fd.get("app_name")),
      email_logo_url: String(fd.get("email_logo_url")),
      support_email: String(fd.get("support_email")),
    })
    await saveKey("urls", {
      public_url: String(fd.get("public_url")),
      frontend_url: String(fd.get("frontend_url")),
    })
    await saveKey("features", {
      sefaz_stub: fd.get("sefaz_stub") === "on",
      billing_enforce: fd.get("billing_enforce") === "on",
      openai_enabled: fd.get("openai_enabled") === "on",
      mp_sandbox: fd.get("mp_sandbox") === "on",
    })
    await saveKey("fiscal", { timezone: String(fd.get("timezone")) })
  }

  const s = data?.settings ?? {}

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Super Admin — Configurações</h1>
        <p className="text-sm text-slate-600">Preferências da plataforma no banco. Segredos permanecem no `.env`.</p>
      </div>
      <Tabs tabs={SUPER_TABS} active={tab} onChange={setTab} />
      {message && <p className="text-sm text-emerald-700">{message}</p>}
      {error && <p className="text-sm text-red-700">{error}</p>}

      {tab === "plataforma" && (
        <form onSubmit={handlePlataforma} className="space-y-4 rounded-xl border bg-white p-5">
          <h2 className="font-semibold">Branding e URLs</h2>
          <div className="grid gap-3 md:grid-cols-2">
            <input name="app_name" defaultValue={String(s.branding?.app_name ?? "")} placeholder="Nome do app" className="rounded-lg border px-3 py-2 text-sm" />
            <input name="support_email" defaultValue={String(s.branding?.support_email ?? "")} placeholder="E-mail suporte" className="rounded-lg border px-3 py-2 text-sm" />
            <input name="email_logo_url" defaultValue={String(s.branding?.email_logo_url ?? "")} placeholder="URL logo e-mail" className="rounded-lg border px-3 py-2 text-sm md:col-span-2" />
            <input name="public_url" defaultValue={String(s.urls?.public_url ?? "")} placeholder="URL pública API" className="rounded-lg border px-3 py-2 text-sm" />
            <input name="frontend_url" defaultValue={String(s.urls?.frontend_url ?? "")} placeholder="URL frontend" className="rounded-lg border px-3 py-2 text-sm" />
            <input name="timezone" defaultValue={String(s.fiscal?.timezone ?? "America/Sao_Paulo")} placeholder="Timezone fiscal" className="rounded-lg border px-3 py-2 text-sm md:col-span-2" />
          </div>
          <h2 className="pt-2 font-semibold">Feature flags</h2>
          <div className="flex flex-wrap gap-4 text-sm">
            <label className="flex items-center gap-2"><input name="sefaz_stub" type="checkbox" defaultChecked={Boolean(s.features?.sefaz_stub)} /> SEFAZ stub</label>
            <label className="flex items-center gap-2"><input name="billing_enforce" type="checkbox" defaultChecked={Boolean(s.features?.billing_enforce)} /> Bloquear sem plano</label>
            <label className="flex items-center gap-2"><input name="openai_enabled" type="checkbox" defaultChecked={Boolean(s.features?.openai_enabled)} /> OpenAI</label>
            <label className="flex items-center gap-2"><input name="mp_sandbox" type="checkbox" defaultChecked={Boolean(s.features?.mp_sandbox)} /> MP sandbox</label>
          </div>
          <button type="submit" className="rounded-lg bg-brand-600 px-4 py-2 text-sm text-white">Salvar plataforma</button>
        </form>
      )}

      {tab === "suporte" && (
        <form
          onSubmit={async (e) => {
            e.preventDefault()
            const fd = new FormData(e.currentTarget)
            await saveKey("support", {
              enabled: fd.get("enabled") === "on",
              url: String(fd.get("url")),
              company: String(fd.get("company")),
              dept: String(fd.get("dept")),
              link_label: String(fd.get("link_label")),
            })
          }}
          className="space-y-3 rounded-xl border bg-white p-5"
        >
          <h2 className="font-semibold">ResolveTickets / Suporte</h2>
          <label className="flex items-center gap-2 text-sm"><input name="enabled" type="checkbox" defaultChecked={Boolean(s.support?.enabled)} /> Habilitar botão no painel</label>
          <input name="url" defaultValue={String(s.support?.url ?? "")} placeholder="URL ResolveTickets" className="w-full rounded-lg border px-3 py-2 text-sm" />
          <input name="company" defaultValue={String(s.support?.company ?? "")} placeholder="Company" className="w-full rounded-lg border px-3 py-2 text-sm" />
          <input name="dept" defaultValue={String(s.support?.dept ?? "")} placeholder="Departamento" className="w-full rounded-lg border px-3 py-2 text-sm" />
          <input name="link_label" defaultValue={String(s.support?.link_label ?? "Abrir chamado")} placeholder="Texto do botão" className="w-full rounded-lg border px-3 py-2 text-sm" />
          <button type="submit" className="rounded-lg bg-brand-600 px-4 py-2 text-sm text-white">Salvar suporte</button>
        </form>
      )}

      {tab === "email" && (
        <div className="space-y-4 rounded-xl border bg-white p-5">
          <h2 className="font-semibold">SMTP (host/porta no banco; senha no .env)</h2>
          <p className="text-sm text-slate-600">SMTP configurado: {data?.env_fallback.smtp_configured ? "sim" : "não"}</p>
          <form
            onSubmit={async (e) => {
              e.preventDefault()
              const fd = new FormData(e.currentTarget)
              await saveKey("smtp", {
                host: String(fd.get("host")),
                port: Number(fd.get("port")),
                secure: fd.get("secure") === "on",
                from: String(fd.get("from")),
              })
            }}
            className="grid gap-3 md:grid-cols-2"
          >
            <input name="host" defaultValue={String(s.smtp?.host ?? "")} placeholder="Host SMTP" className="rounded-lg border px-3 py-2 text-sm" />
            <input name="port" type="number" defaultValue={Number(s.smtp?.port ?? 465)} className="rounded-lg border px-3 py-2 text-sm" />
            <input name="from" defaultValue={String(s.smtp?.from ?? "")} placeholder="From" className="rounded-lg border px-3 py-2 text-sm md:col-span-2" />
            <label className="flex items-center gap-2 text-sm"><input name="secure" type="checkbox" defaultChecked={s.smtp?.secure !== false} /> TLS implícito (465)</label>
            <button type="submit" className="rounded-lg bg-brand-600 px-4 py-2 text-sm text-white md:col-span-2">Salvar SMTP</button>
          </form>
          <div className="flex gap-2">
            <input value={testEmail} onChange={(e) => setTestEmail(e.target.value)} placeholder="e-mail teste" className="flex-1 rounded-lg border px-3 py-2 text-sm" />
            <button
              type="button"
              onClick={() => apiFetch("/v1/admin/settings/email/test", { method: "POST", body: JSON.stringify({ to: testEmail }) }).then(() => setMessage("E-mail teste enviado"))}
              className="rounded-lg border px-4 py-2 text-sm"
            >
              Enviar teste
            </button>
          </div>
        </div>
      )}

      {tab === "operacao" && health && (
        <div className="grid gap-3 sm:grid-cols-2">
          {Object.entries(health).map(([k, v]) => (
            <div key={k} className="rounded-xl border bg-white p-4 text-sm">
              <span className="font-medium">{k}:</span> {typeof v === "object" ? JSON.stringify(v) : String(v)}
            </div>
          ))}
          <button
            type="button"
            onClick={() => apiFetch<{ alertas_enviados: number }>("/v1/admin/ops/certificados/alertas-vencimento", { method: "POST" }).then((r) => setMessage(`${r.alertas_enviados} alertas`))}
            className="rounded-lg bg-brand-600 px-4 py-2 text-sm text-white sm:col-span-2"
          >
            Disparar alertas certificado
          </button>
        </div>
      )}

      {tab === "billing" && (
        <div className="rounded-xl border bg-white p-5 text-sm space-y-3">
          <p>Token MP: {data?.env_fallback.mp_configured ? "configurado no .env" : "não configurado"}</p>
          <button type="button" onClick={() => apiFetch("/v1/admin/settings/integrations/mp-health").then((r) => setMessage(JSON.stringify(r)))} className="rounded-lg border px-4 py-2">
            Testar Mercado Pago
          </button>
          <p className="text-slate-600">Atribuição manual de plano via API admin billing ou painel Gestão.</p>
        </div>
      )}

      {tab === "tenancy" && (
        <div className="space-y-4">
          <form
            onSubmit={async (e) => {
              e.preventDefault()
              const form = e.currentTarget
              setError("")
              setMessage("")
              const fd = new FormData(form)
              const nome = String(fd.get("org_nome")).trim()
              if (nome.length < 2) {
                setError("Nome da organização deve ter ao menos 2 caracteres.")
                return
              }
              setOrgSaving(true)
              try {
                const created = await apiFetch<{ id: string; nome: string }>("/v1/admin/organizacoes", {
                  method: "POST",
                  body: JSON.stringify({ nome }),
                })
                setMessage(`Organização criada: ${created.nome}`)
                form.reset()
                await reloadOrgs()
                setSelectedOrg(created.id)
              } catch (err) {
                setError(parseApiError(err))
              } finally {
                setOrgSaving(false)
              }
            }}
            className="flex flex-col gap-3 rounded-xl border bg-white p-5 sm:flex-row sm:items-end"
          >
            <div className="flex-1 space-y-1">
              <h2 className="font-semibold text-slate-900">Nova organização</h2>
              <input name="org_nome" required minLength={2} maxLength={150} placeholder="Nome da organização *" className="w-full rounded-lg border px-3 py-2 text-sm" />
            </div>
            <button type="submit" disabled={orgSaving} className="rounded-lg bg-brand-600 px-4 py-2 text-sm text-white disabled:opacity-60">
              {orgSaving ? "Salvando…" : "Criar organização"}
            </button>
          </form>

          <div className="space-y-3 rounded-xl border bg-white p-5">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <label htmlFor="tenancy-org-select" className="text-sm font-medium text-slate-700 shrink-0">
                Organização ativa
              </label>
              <select
                id="tenancy-org-select"
                value={selectedOrg}
                onChange={(e) => {
                  setError("")
                  setSelectedOrg(e.target.value)
                }}
                className="flex-1 rounded-lg border px-3 py-2 text-sm"
              >
                <option value="">Selecione organização</option>
                {orgs.map((o) => (
                  <option key={o.id} value={o.id}>{o.nome}{o.ativo ? "" : " (arquivada)"}</option>
                ))}
              </select>
            </div>
            {selectedOrg && (
              <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
                <label className="flex flex-1 flex-col gap-1 text-sm">
                  <span className="text-slate-700">Nome da organização</span>
                  <input
                    value={orgNomeDraft}
                    onChange={(e) => setOrgNomeDraft(e.target.value)}
                    minLength={2}
                    maxLength={150}
                    className="rounded-lg border px-3 py-2 text-sm"
                    aria-label="Nome da organização"
                  />
                </label>
                <button
                  type="button"
                  disabled={orgSaving}
                  onClick={async () => {
                    const nome = orgNomeDraft.trim()
                    const row = orgs.find((org) => org.id === selectedOrg)
                    if (!row) return
                    if (nome.length < 2) {
                      setError("Nome da organização deve ter ao menos 2 caracteres.")
                      return
                    }
                    setError("")
                    setMessage("")
                    setOrgSaving(true)
                    try {
                      await apiFetch(`/v1/admin/organizacoes/${selectedOrg}`, {
                        method: "PATCH",
                        body: JSON.stringify({ nome, ativo: row.ativo }),
                      })
                      setMessage(`Organização atualizada: ${nome}`)
                      await reloadOrgs()
                    } catch (err) {
                      setError(parseApiError(err))
                    } finally {
                      setOrgSaving(false)
                    }
                  }}
                  className="rounded-lg bg-brand-600 px-4 py-2 text-sm text-white disabled:opacity-60"
                >
                  {orgSaving ? "Salvando…" : "Salvar organização"}
                </button>
                <button
                  type="button"
                  disabled={orgSaving}
                  onClick={async () => {
                    const row = orgs.find((org) => org.id === selectedOrg)
                    if (!row) return
                    const nextAtivo = !row.ativo
                    const confirmText = nextAtivo
                      ? "Restaurar esta organização? Grupos e empresas arquivados junto continuam arquivados até você restaurar cada um."
                      : "Arquivar esta organização? Grupos e empresas dela também serão arquivados. Notas e certificados permanecem."
                    if (!window.confirm(confirmText)) return
                    setError("")
                    setMessage("")
                    setOrgSaving(true)
                    try {
                      await apiFetch(`/v1/admin/organizacoes/${selectedOrg}`, {
                        method: "PATCH",
                        body: JSON.stringify({ nome: row.nome, ativo: nextAtivo }),
                      })
                      setMessage(nextAtivo ? "Organização restaurada." : "Organização arquivada.")
                      await reloadOrgs()
                      await reloadTenancy(selectedOrg)
                      await reloadEmpresaContext()
                    } catch (err) {
                      setError(parseApiError(err))
                    } finally {
                      setOrgSaving(false)
                    }
                  }}
                  className="rounded-lg border border-amber-700 px-4 py-2 text-sm text-amber-800 disabled:opacity-60"
                >
                  {orgs.find((org) => org.id === selectedOrg)?.ativo ? "Arquivar organização" : "Restaurar organização"}
                </button>
              </div>
            )}
          </div>

          {selectedOrg && (
            <form
              onSubmit={async (e) => {
                e.preventDefault()
                const form = e.currentTarget
                setError("")
                setMessage("")
                const fd = new FormData(form)
                const nome = String(fd.get("grupo_nome")).trim()
                if (nome.length < 2) {
                  setError("Nome do grupo deve ter ao menos 2 caracteres.")
                  return
                }
                setGrupoSaving(true)
                try {
                  const created = await apiFetch<{ id: string; nome: string }>(
                    `/v1/admin/organizacoes/${selectedOrg}/grupos`,
                    { method: "POST", body: JSON.stringify({ nome }) },
                  )
                  setMessage(`Grupo criado: ${created.nome}`)
                  form.reset()
                  await reloadTenancy(selectedOrg)
                } catch (err) {
                  setError(parseApiError(err))
                } finally {
                  setGrupoSaving(false)
                }
              }}
              className="flex flex-col gap-3 rounded-xl border bg-white p-5 sm:flex-row sm:items-end"
            >
              <div className="flex-1 space-y-1">
                <h2 className="font-semibold text-slate-900">Novo grupo de empresas</h2>
                <input name="grupo_nome" required minLength={2} maxLength={150} placeholder="Nome do grupo *" className="w-full rounded-lg border px-3 py-2 text-sm" />
              </div>
              <button type="submit" disabled={grupoSaving} className="rounded-lg border border-brand-600 px-4 py-2 text-sm text-brand-700 disabled:opacity-60">
                {grupoSaving ? "Salvando…" : "Criar grupo"}
              </button>
            </form>
          )}

          {selectedOrg && (
            <form
              ref={empresaFormRef}
              onSubmit={async (e) => {
                e.preventDefault()
                setError("")
                setMessage("")
                const cnpj = onlyDigits(empresaForm.cnpj)
                const codigoMunicipio = onlyDigits(empresaForm.codigo_municipio)
                if (cnpj.length !== 14) {
                  setError("CNPJ deve ter 14 dígitos (somente números).")
                  return
                }
                if (codigoMunicipio.length !== 7) {
                  setError("Código IBGE do município deve ter 7 dígitos.")
                  return
                }
                const cep = onlyDigits(empresaForm.cep)
                if (cep.length !== 8) {
                  setError("CEP deve ter 8 dígitos.")
                  return
                }
                const editing = empresas.find((emp) => emp.id === editingEmpresaId)
                const payload = {
                  razao_social: empresaForm.razao_social.trim(),
                  nome_fantasia: empresaForm.nome_fantasia.trim() || null,
                  cnpj,
                  inscricao_estadual: empresaForm.inscricao_estadual.trim(),
                  uf: empresaForm.uf,
                  codigo_municipio: codigoMunicipio,
                  municipio: (empresaForm.municipio || municipioNome).trim(),
                  logradouro: empresaForm.logradouro.trim(),
                  numero: empresaForm.numero.trim(),
                  complemento: empresaForm.complemento.trim(),
                  bairro: empresaForm.bairro.trim(),
                  cep,
                  telefone: onlyDigits(empresaForm.telefone),
                  regime_tributario: Number(empresaForm.regime_tributario),
                  grupo_id: empresaForm.grupo_id || null,
                  ativo: editing ? editing.ativo : true,
                }
                setEmpresaSaving(true)
                try {
                  const saved = await apiFetch<{ razao_social: string }>(
                    editingEmpresaId
                      ? `/v1/admin/organizacoes/${selectedOrg}/empresas/${editingEmpresaId}`
                      : `/v1/admin/organizacoes/${selectedOrg}/empresas`,
                    { method: editingEmpresaId ? "PATCH" : "POST", body: JSON.stringify(payload) },
                  )
                  setMessage(editingEmpresaId ? `Empresa atualizada: ${saved.razao_social}` : `Empresa cadastrada: ${saved.razao_social}`)
                  setEditingEmpresaId(null)
                  setEmpresaForm(emptyEmpresaForm())
                  setMunicipioNome("")
                  setCnpjAvisos([])
                  await reloadTenancy(selectedOrg)
                  await reloadEmpresaContext()
                } catch (err) {
                  setError(parseApiError(err))
                } finally {
                  setEmpresaSaving(false)
                }
              }}
              className="space-y-3 rounded-xl border bg-white p-5"
            >
              <h2 className="font-semibold text-slate-900">{editingEmpresaId ? "Editar empresa" : "Nova empresa"}</h2>
              <p className="text-xs text-slate-600">
                Consulte o CNPJ para preencher a ficha. Os campos continuam editáveis antes de cadastrar. NF-e nesta versão exige UF <strong>SP</strong>.
              </p>
              <div className="grid gap-3 md:grid-cols-2">
                <label className="flex flex-col gap-1 text-sm md:col-span-2">
                  <span className="text-slate-700">CNPJ</span>
                  <span className="flex flex-col gap-2 sm:flex-row">
                    <input
                      name="cnpj"
                      required
                      value={empresaForm.cnpj}
                      onChange={(e) => setEmpresaForm((prev) => ({ ...prev, cnpj: e.target.value }))}
                      placeholder="00.000.000/0000-00"
                      className="w-full rounded-lg border px-3 py-2 text-sm"
                      inputMode="numeric"
                      aria-label="CNPJ"
                    />
                    <button
                      type="button"
                      disabled={cnpjLoading}
                      onClick={async () => {
                        const cnpj = onlyDigits(empresaForm.cnpj)
                        setError("")
                        setMessage("")
                        if (cnpj.length !== 14) {
                          setError("CNPJ deve ter 14 dígitos (somente números).")
                          return
                        }
                        setCnpjLoading(true)
                        try {
                          const prefill = await apiFetch<CnpjPrefill>(`/v1/admin/cnpj/${cnpj}`)
                          setEmpresaForm((prev) => ({
                            ...prev,
                            razao_social: prefill.razao_social,
                            nome_fantasia: prefill.nome_fantasia ?? "",
                            cnpj: prefill.cnpj,
                            inscricao_estadual: prefill.inscricao_estadual,
                            uf: prefill.uf || prev.uf,
                            codigo_municipio: prefill.codigo_municipio,
                            municipio: prefill.municipio_nome ?? "",
                            logradouro: prefill.logradouro ?? "",
                            numero: prefill.numero ?? "",
                            complemento: prefill.complemento ?? "",
                            bairro: prefill.bairro ?? "",
                            cep: prefill.cep ?? "",
                            telefone: prefill.telefone ?? "",
                            regime_tributario: String(prefill.regime_tributario || 3),
                          }))
                          setMunicipioNome(prefill.municipio_nome ?? "")
                          setCnpjAvisos(prefill.avisos ?? [])
                          setMessage("Dados do CNPJ preenchidos. Altere o que precisar antes de cadastrar.")
                        } catch (err) {
                          setError(parseApiError(err))
                        } finally {
                          setCnpjLoading(false)
                        }
                      }}
                      className="shrink-0 rounded-lg border border-brand-600 px-4 py-2 text-sm text-brand-700 disabled:opacity-60"
                    >
                      {cnpjLoading ? "Consultando…" : "Consultar CNPJ"}
                    </button>
                  </span>
                </label>
                <label className="flex flex-col gap-1 text-sm md:col-span-2">
                  <span className="text-slate-700">Razão social</span>
                  <input
                    name="razao_social"
                    required
                    minLength={2}
                    value={empresaForm.razao_social}
                    onChange={(e) => setEmpresaForm((prev) => ({ ...prev, razao_social: e.target.value }))}
                    className="rounded-lg border px-3 py-2 text-sm"
                    aria-label="Razão social"
                  />
                </label>
                <label className="flex flex-col gap-1 text-sm md:col-span-2">
                  <span className="text-slate-700">Nome fantasia</span>
                  <input
                    name="nome_fantasia"
                    value={empresaForm.nome_fantasia}
                    onChange={(e) => setEmpresaForm((prev) => ({ ...prev, nome_fantasia: e.target.value }))}
                    className="rounded-lg border px-3 py-2 text-sm"
                    aria-label="Nome fantasia"
                  />
                </label>
                <label className="flex flex-col gap-1 text-sm">
                  <span className="text-slate-700">Inscrição estadual</span>
                  <input
                    name="inscricao_estadual"
                    required
                    value={empresaForm.inscricao_estadual}
                    onChange={(e) => setEmpresaForm((prev) => ({ ...prev, inscricao_estadual: e.target.value }))}
                    className="rounded-lg border px-3 py-2 text-sm"
                    aria-label="Inscrição estadual"
                  />
                </label>
                <label className="flex flex-col gap-1 text-sm">
                  <span className="text-slate-700">UF</span>
                  <select
                    name="uf"
                    required
                    value={empresaForm.uf}
                    onChange={(e) => {
                      setMunicipioNome("")
                      setEmpresaForm((prev) => ({
                        ...prev,
                        uf: e.target.value,
                        codigo_municipio: "",
                        municipio: "",
                      }))
                    }}
                    className="rounded-lg border px-3 py-2 text-sm"
                    aria-label="UF"
                  >
                    {UF_OPTIONS.map((uf) => (
                      <option key={uf} value={uf}>{uf}</option>
                    ))}
                  </select>
                </label>
                <label className="flex flex-col gap-1 text-sm md:col-span-2">
                  <span className="text-slate-700">Município (código IBGE)</span>
                  <MunicipioIbgeSelect
                    uf={empresaForm.uf}
                    ibge={empresaForm.codigo_municipio}
                    municipioNome={municipioNome}
                    onSelect={(code, nome) => {
                      setEmpresaForm((prev) => ({ ...prev, codigo_municipio: code, municipio: nome }))
                      setMunicipioNome(nome)
                    }}
                  />
                </label>
                <label className="flex flex-col gap-1 text-sm md:col-span-2">
                  <span className="text-slate-700">Logradouro</span>
                  <input
                    name="logradouro"
                    required
                    maxLength={60}
                    value={empresaForm.logradouro}
                    onChange={(e) => setEmpresaForm((prev) => ({ ...prev, logradouro: e.target.value }))}
                    className="rounded-lg border px-3 py-2 text-sm"
                    aria-label="Logradouro"
                  />
                </label>
                <label className="flex flex-col gap-1 text-sm">
                  <span className="text-slate-700">Número</span>
                  <input
                    name="numero"
                    required
                    maxLength={60}
                    value={empresaForm.numero}
                    onChange={(e) => setEmpresaForm((prev) => ({ ...prev, numero: e.target.value }))}
                    className="rounded-lg border px-3 py-2 text-sm"
                    aria-label="Número"
                  />
                </label>
                <label className="flex flex-col gap-1 text-sm md:col-span-2">
                  <span className="text-slate-700">Complemento</span>
                  <input
                    name="complemento"
                    maxLength={60}
                    value={empresaForm.complemento}
                    onChange={(e) => setEmpresaForm((prev) => ({ ...prev, complemento: e.target.value }))}
                    className="rounded-lg border px-3 py-2 text-sm"
                    aria-label="Complemento"
                    placeholder="Sala, bloco, conjunto"
                  />
                </label>
                <label className="flex flex-col gap-1 text-sm">
                  <span className="text-slate-700">Bairro</span>
                  <input
                    name="bairro"
                    required
                    maxLength={60}
                    value={empresaForm.bairro}
                    onChange={(e) => setEmpresaForm((prev) => ({ ...prev, bairro: e.target.value }))}
                    className="rounded-lg border px-3 py-2 text-sm"
                    aria-label="Bairro"
                  />
                </label>
                <label className="flex flex-col gap-1 text-sm">
                  <span className="text-slate-700">CEP</span>
                  <input
                    name="cep"
                    required
                    value={empresaForm.cep}
                    onChange={(e) => setEmpresaForm((prev) => ({ ...prev, cep: e.target.value }))}
                    className="rounded-lg border px-3 py-2 text-sm"
                    inputMode="numeric"
                    aria-label="CEP"
                  />
                </label>
                <label className="flex flex-col gap-1 text-sm">
                  <span className="text-slate-700">Telefone</span>
                  <input
                    name="telefone"
                    value={empresaForm.telefone}
                    onChange={(e) => setEmpresaForm((prev) => ({ ...prev, telefone: e.target.value }))}
                    className="rounded-lg border px-3 py-2 text-sm"
                    inputMode="tel"
                    aria-label="Telefone"
                  />
                </label>
                <label className="flex flex-col gap-1 text-sm">
                  <span className="text-slate-700">Regime tributário</span>
                  <select
                    name="regime_tributario"
                    value={empresaForm.regime_tributario}
                    onChange={(e) => setEmpresaForm((prev) => ({ ...prev, regime_tributario: e.target.value }))}
                    className="rounded-lg border px-3 py-2 text-sm"
                    aria-label="Regime tributário"
                  >
                    <option value="1">Regime 1 — Simples Nacional</option>
                    <option value="2">Regime 2 — Simples (excesso sublimite)</option>
                    <option value="3">Regime 3 — Regime normal</option>
                  </select>
                </label>
                <label className="flex flex-col gap-1 text-sm md:col-span-2">
                  <span className="text-slate-700">Grupo</span>
                  <select
                    name="grupo_id"
                    value={empresaForm.grupo_id}
                    onChange={(e) => setEmpresaForm((prev) => ({ ...prev, grupo_id: e.target.value }))}
                    className="rounded-lg border px-3 py-2 text-sm"
                    aria-label="Grupo"
                  >
                    <option value="">Sem grupo</option>
                    {grupos.filter((g) => g.ativo || g.id === empresaForm.grupo_id).map((g) => (
                      <option key={g.id} value={g.id}>{g.nome}{g.ativo ? "" : " (arquivado)"}</option>
                    ))}
                  </select>
                </label>
              </div>
              {cnpjAvisos.length > 0 && (
                <ul className="list-disc space-y-1 pl-5 text-xs text-amber-800">
                  {cnpjAvisos.map((aviso) => (
                    <li key={aviso}>{aviso}</li>
                  ))}
                </ul>
              )}
              <div className="flex flex-wrap gap-2">
                <button
                  type="submit"
                  disabled={empresaSaving || cnpjLoading}
                  className="rounded-lg bg-brand-600 px-4 py-2 text-sm text-white disabled:opacity-60"
                >
                  {empresaSaving ? "Salvando…" : editingEmpresaId ? "Salvar alterações" : "Cadastrar empresa"}
                </button>
                {editingEmpresaId && (
                  <button
                    type="button"
                    onClick={() => {
                      setEditingEmpresaId(null)
                      setEmpresaForm(emptyEmpresaForm())
                      setMunicipioNome("")
                      setCnpjAvisos([])
                    }}
                    className="rounded-lg border px-4 py-2 text-sm text-slate-700"
                  >
                    Cancelar edição
                  </button>
                )}
              </div>
            </form>
          )}

          <p className="text-xs text-slate-500">
            Arquivar tira o cadastro do uso diário. Notas, certificados e o CNPJ permanecem. Restaurar devolve o acesso.
          </p>
          <div className="grid gap-4 lg:grid-cols-3">
            <div className="rounded-xl border bg-white p-4">
              <h3 className="font-semibold mb-2">Empresas</h3>
              {empresas.length === 0 ? (
                <p className="text-sm text-slate-500">Nenhuma empresa nesta organização.</p>
              ) : (
                <ul className="text-sm divide-y">
                  {empresas.map((emp) => (
                    <li key={emp.id} className="flex flex-col gap-2 py-3">
                      <span>
                        {emp.razao_social} — {emp.cnpj}
                        <span className="text-slate-500"> ({emp.uf})</span>
                        {!emp.ativo && <span className="ml-2 rounded bg-slate-200 px-1.5 py-0.5 text-xs text-slate-700">Arquivada</span>}
                      </span>
                      <span className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={() => {
                            setEditingEmpresaId(emp.id)
                            setEmpresaForm({
                              razao_social: emp.razao_social,
                              nome_fantasia: emp.nome_fantasia ?? "",
                              cnpj: emp.cnpj,
                              inscricao_estadual: emp.inscricao_estadual,
                              uf: emp.uf,
                              codigo_municipio: emp.codigo_municipio,
                              municipio: emp.municipio ?? "",
                              logradouro: emp.logradouro ?? "",
                              numero: emp.numero ?? "",
                              complemento: emp.complemento ?? "",
                              bairro: emp.bairro ?? "",
                              cep: emp.cep ?? "",
                              telefone: emp.telefone ?? "",
                              regime_tributario: String(emp.regime_tributario || 1),
                              grupo_id: emp.grupo_id ?? "",
                            })
                            setMunicipioNome(emp.municipio ?? "")
                            setCnpjAvisos([])
                            setError("")
                            setMessage("")
                            empresaFormRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })
                          }}
                          className="rounded border px-2 py-1 text-xs text-slate-700"
                        >
                          Editar
                        </button>
                        <button
                          type="button"
                          onClick={async () => {
                            const nextAtivo = !emp.ativo
                            const confirmText = nextAtivo
                              ? `Restaurar ${emp.razao_social}?`
                              : `Arquivar ${emp.razao_social}? Ela sai do seletor e não poderá emitir até ser restaurada.`
                            if (!window.confirm(confirmText)) return
                            setError("")
                            setMessage("")
                            try {
                              await apiFetch(`/v1/admin/organizacoes/${selectedOrg}/empresas/${emp.id}`, {
                                method: "PATCH",
                                body: JSON.stringify({
                                  razao_social: emp.razao_social,
                                  nome_fantasia: emp.nome_fantasia,
                                  cnpj: emp.cnpj,
                                  inscricao_estadual: emp.inscricao_estadual,
                                  uf: emp.uf,
                                  codigo_municipio: emp.codigo_municipio,
                                  municipio: emp.municipio,
                                  logradouro: emp.logradouro,
                                  numero: emp.numero,
                                  complemento: emp.complemento,
                                  bairro: emp.bairro,
                                  cep: emp.cep,
                                  telefone: emp.telefone,
                                  regime_tributario: emp.regime_tributario,
                                  grupo_id: emp.grupo_id,
                                  ativo: nextAtivo,
                                }),
                              })
                              if (editingEmpresaId === emp.id) setEditingEmpresaId(null)
                              setMessage(nextAtivo ? "Empresa restaurada." : "Empresa arquivada.")
                              await reloadTenancy(selectedOrg)
                              await reloadEmpresaContext()
                            } catch (err) {
                              setError(parseApiError(err))
                            }
                          }}
                          className="rounded border border-amber-700 px-2 py-1 text-xs text-amber-800"
                        >
                          {emp.ativo ? "Arquivar" : "Restaurar"}
                        </button>
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="rounded-xl border bg-white p-4">
              <h3 className="font-semibold mb-2">Grupos</h3>
              {!selectedOrg ? (
                <p className="text-sm text-slate-500">Selecione uma organização.</p>
              ) : grupos.length === 0 ? (
                <p className="text-sm text-slate-500">Nenhum grupo. Crie um acima para agrupar CNPJs.</p>
              ) : (
                <ul className="text-sm divide-y">
                  {grupos.map((g) => (
                    <li key={g.id} className="flex flex-col gap-2 py-3">
                      {editingGrupoId === g.id ? (
                        <form
                          className="flex flex-col gap-2"
                          onSubmit={async (e) => {
                            e.preventDefault()
                            const nome = grupoNomeDraft.trim()
                            if (nome.length < 2) {
                              setError("Nome do grupo deve ter ao menos 2 caracteres.")
                              return
                            }
                            setError("")
                            setMessage("")
                            setGrupoSaving(true)
                            try {
                              await apiFetch(`/v1/admin/organizacoes/${selectedOrg}/grupos/${g.id}`, {
                                method: "PATCH",
                                body: JSON.stringify({ nome, ativo: g.ativo }),
                              })
                              setEditingGrupoId(null)
                              setMessage(`Grupo atualizado: ${nome}`)
                              await reloadTenancy(selectedOrg)
                            } catch (err) {
                              setError(parseApiError(err))
                            } finally {
                              setGrupoSaving(false)
                            }
                          }}
                        >
                          <input
                            value={grupoNomeDraft}
                            onChange={(e) => setGrupoNomeDraft(e.target.value)}
                            minLength={2}
                            maxLength={150}
                            className="rounded-lg border px-3 py-2 text-sm"
                            aria-label="Nome do grupo"
                          />
                          <span className="flex gap-2">
                            <button type="submit" disabled={grupoSaving} className="rounded bg-brand-600 px-2 py-1 text-xs text-white disabled:opacity-60">Salvar</button>
                            <button type="button" onClick={() => setEditingGrupoId(null)} className="rounded border px-2 py-1 text-xs">Cancelar</button>
                          </span>
                        </form>
                      ) : (
                        <>
                          <span>
                            {g.nome}
                            {!g.ativo && <span className="ml-2 rounded bg-slate-200 px-1.5 py-0.5 text-xs text-slate-700">Arquivado</span>}
                          </span>
                          <span className="flex flex-wrap gap-2">
                            <button
                              type="button"
                              onClick={() => {
                                setEditingGrupoId(g.id)
                                setGrupoNomeDraft(g.nome)
                              }}
                              className="rounded border px-2 py-1 text-xs text-slate-700"
                            >
                              Editar
                            </button>
                            <button
                              type="button"
                              onClick={async () => {
                                const nextAtivo = !g.ativo
                                const confirmText = nextAtivo ? `Restaurar o grupo ${g.nome}?` : `Arquivar o grupo ${g.nome}? As empresas continuam cadastradas.`
                                if (!window.confirm(confirmText)) return
                                setError("")
                                setMessage("")
                                try {
                                  await apiFetch(`/v1/admin/organizacoes/${selectedOrg}/grupos/${g.id}`, {
                                    method: "PATCH",
                                    body: JSON.stringify({ nome: g.nome, ativo: nextAtivo }),
                                  })
                                  setMessage(nextAtivo ? "Grupo restaurado." : "Grupo arquivado.")
                                  await reloadTenancy(selectedOrg)
                                } catch (err) {
                                  setError(parseApiError(err))
                                }
                              }}
                              className="rounded border border-amber-700 px-2 py-1 text-xs text-amber-800"
                            >
                              {g.ativo ? "Arquivar" : "Restaurar"}
                            </button>
                          </span>
                        </>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="rounded-xl border bg-white p-4">
              <h3 className="font-semibold mb-2">Usuários / papéis</h3>
              {!selectedOrg ? (
                <p className="text-sm text-slate-500">Selecione uma organização.</p>
              ) : memberships.length === 0 ? (
                <p className="text-sm text-slate-500">Nenhum vínculo nesta organização.</p>
              ) : (
                <ul className="text-sm divide-y">
                  {memberships.map((m) => (
                    <li key={m.id} className="flex flex-col gap-2 py-3">
                      <span>
                        {m.email} — {m.papel}
                        {!m.ativo && <span className="ml-2 rounded bg-slate-200 px-1.5 py-0.5 text-xs text-slate-700">Arquivado</span>}
                      </span>
                      <button
                        type="button"
                        onClick={async () => {
                          const nextAtivo = !m.ativo
                          const confirmText = nextAtivo
                            ? `Restaurar o acesso de ${m.email}?`
                            : `Arquivar o vínculo de ${m.email}? Esse usuário perde o acesso deste papel.`
                          if (!window.confirm(confirmText)) return
                          setError("")
                          setMessage("")
                          try {
                            await apiFetch(`/v1/admin/organizacoes/${selectedOrg}/memberships/${m.id}`, {
                              method: "PATCH",
                              body: JSON.stringify({ ativo: nextAtivo }),
                            })
                            setMessage(nextAtivo ? "Vínculo restaurado." : "Vínculo arquivado.")
                            await reloadTenancy(selectedOrg)
                          } catch (err) {
                            setError(parseApiError(err))
                          }
                        }}
                        className="w-fit rounded border border-amber-700 px-2 py-1 text-xs text-amber-800"
                      >
                        {m.ativo ? "Arquivar vínculo" : "Restaurar vínculo"}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
